import { sortBy, uniqBy, orderBy } from 'lodash';
import {
  bundleInsightOrder,
  PORTFOLIO_PERFORMANCE_CATEGORY,
  PORTFOLIO_INVENTORY_CATEGORY,
  ppRegexFilter,
} from '../constants';
import getUomSymbol, {
  convertUnitBasedOnSourceAndTargetUnitFamily,
} from '../views/containers/ParameterLive/components/uomUtils';

export const getFlattenArray = dataArr =>
  Array.from(new Set(dataArr.map(s => s?.subFamily?.id))).map(lab => ({
    label: lab,
    data: dataArr
      .filter(s => s?.subFamily?.id === lab)
      .map(edition => edition.assetCats)
      .flat(),
  }));

export const filterSitesWithInsightSubfamily = (siteList, insightSubfamily) =>
  siteList?.filter(ele => {
    const elementWithRequiredInsight = ele?.sum?.filter(
      sumEl => sumEl?.subFamily?.id === insightSubfamily,
    );
    return elementWithRequiredInsight.length > 0;
  });

export const getSiteListDataForCategory = (siteList, insightSubfamily) => {
  const filteredSiteList = filterSitesWithInsightSubfamily(
    siteList,
    insightSubfamily,
  );
  return filteredSiteList?.map(ele => {
    const flattenData = getFlattenArray(ele?.sum)?.filter(
      obj => obj.label === insightSubfamily,
    );
    return {
      ...ele,
      equipInsight: flattenData?.[0]?.data,
      ...(insightSubfamily === PORTFOLIO_PERFORMANCE_CATEGORY && {
        sum: sortBy(ele?.sum, o =>
          bundleInsightOrder.indexOf(o?.subFamily?.id),
        ),
      }),
    };
  });
};

export const getEquipmentCategory = (siteList, insightSubfamily) => {
  const equipmentCategories = [];
  const flattenArray = getSiteListDataForCategory(siteList, insightSubfamily);
  flattenArray?.forEach(obj =>
    obj?.equipInsight?.forEach(ele => equipmentCategories.push(ele)),
  );
  return uniqBy(
    sortBy(equipmentCategories, o => o.pos),
    o => o.dn,
  ).map(o => o.dn);
};

export const getEquipmentCategoryWithId = (siteList, insightSubfamily) => {
  const equipmentCategories = [];
  const flattenArray = getSiteListDataForCategory(siteList, insightSubfamily);
  flattenArray?.forEach(obj =>
    obj?.equipInsight?.forEach(ele => equipmentCategories.push(ele)),
  );
  return uniqBy(
    sortBy(equipmentCategories, o => o.pos),
    o => o.id,
  ).map(o => ({ id: o.id, dn: o.dn }));
};

export const getPortfolioData = svcBundles =>
  orderBy(
    svcBundles?.map(ele => {
      const flattenEquipmentInsightData = getFlattenArray(ele?.summary)?.filter(
        obj => obj.label === PORTFOLIO_PERFORMANCE_CATEGORY,
      );
      const flattenEquipmentInventoryData = getFlattenArray(
        ele?.summary,
      )?.filter(obj => obj.label === PORTFOLIO_INVENTORY_CATEGORY);
      const svcSummary = {
        svcBundle: ele.svcBundle,
        svcBundleSum: sortBy(
          ele?.summary?.map(obj => ({
            ...obj.svcBundleSum,
            type: obj?.subFamily?.id,
          })),
          o => bundleInsightOrder.indexOf(o.type),
        ),
      };
      return {
        ...ele,
        equipInsight: flattenEquipmentInsightData?.[0]?.data,
        equipInventoryInsight: flattenEquipmentInventoryData?.[0]?.data,
        hasInventory: flattenEquipmentInventoryData?.length > 0,
        svcSummary,
        epiOnboarded: Number(
          svcSummary?.svcBundleSum?.[0]?.assetSum?.find(
            item => item?.id === 'ONB',
          )?.count,
        ),
      };
    }),
    o => o?.epiOnboarded,
    'desc',
  );

export const getSingleSubfamilyData = (dataList, summaryNode, filterString) =>
  dataList
    ?.map(data => ({
      ...data,
      [summaryNode]: data?.[summaryNode]?.filter(
        sum => sum?.subFamily?.id === filterString,
      ),
    }))
    .filter(data => data?.[summaryNode]?.length > 0);

export const getAssetSummaryData = assetSummaryData =>
  assetSummaryData?.[0]?.assetCats?.[0]?.assetSum?.reduce((acc, cur) => {
    acc[cur?.id] = cur;
    return acc;
  }, {});

export const getsvcSummaryData = assetSummaryData =>
  assetSummaryData?.[0]?.svcBundleSum?.assetSum?.reduce((acc, cur) => {
    acc[cur?.id] = cur;
    return acc;
  }, {});

export const getRequiredState = (dataList, summaryNode, filterString) => {
  const subFamilyData = getSingleSubfamilyData(
    dataList,
    summaryNode,
    filterString,
  );
  return getAssetSummaryData(subFamilyData?.[0]?.[summaryNode]);
};

export const getEquipHealthState = (dataList, summaryNode, filterString) => {
  const subFamilyData = getSingleSubfamilyData(
    dataList?.slice(0, 1),
    summaryNode,
    filterString,
  );
  return getsvcSummaryData(subFamilyData?.[0]?.[summaryNode]);
};

export const getAssetSummaryDataTrends = assetSum => {
  const arr = assetSum?.reduce((acc, cur) => {
    acc[cur?.id] = cur?.count;
    return acc;
  }, {});
  return arr;
};

export const getInsightCategoryData = (data, category) =>
  data?.filter(sum => sum?.subFamily?.id === category);

/**
 * Get header label and value from PI data
 * @param {object} PIData
 * @param {string} UOM
 * @returns {array} [headerLabel, headerValue]
 */
export const getPiHeaderLabelAndValue = (PIData, UOM) => {
  let headerLabel = '';
  let headerValue = '';
  const ppdata = PIData.ppList.filter(pp => !ppRegexFilter.test(pp.pp));
  if (ppdata.length > 0) {
    headerLabel = getUomSymbol(ppdata[0].unitFamily, UOM);
    headerValue = convertUnitBasedOnSourceAndTargetUnitFamily(
      ppdata[0].unitFamily,
      UOM,
      ppdata[0].val,
    );
  }
  return [headerLabel, headerValue];
};

export const getCategoryName = (equipmentCategory, catId) =>
  equipmentCategory?.find(equipCat => equipCat.id === catId)?.dn;
